import { useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import { useEffect } from 'react'

import {
  checkObjectProperty,
  extractQuestionOptions,
  getSuburbDefaultValue,
  triggeredQuestions,
} from 'lib/Helper'
import CheckboxQuestion from 'components/SignupFormQuestions/CheckboxQuestion'
import DateQuestion from 'components/SignupFormQuestions/DateQuestion'
import DropdownQuestion from 'components/SignupFormQuestions/DropdownQuestion'
import EmailQuestion from 'components/SignupFormQuestions/EmailQuestion'
import EssayQuestion from 'components/SignupFormQuestions/EssayQuestion'
import ImageQuestion from 'components/SignupFormQuestions/ImageQuestion'
import NumberQuestion from 'components/SignupFormQuestions/NumberQuestion'
import RadioButtonQuestion from 'components/SignupFormQuestions/RadioButtonQuestion'
import RegionQuestion from 'components/SignupFormQuestions/RegionQuestion'
import SectionQuestion from 'components/SignupFormQuestions/SectionQuestion'
import SingleLineQuestion from 'components/SignupFormQuestions/SingleLineQuestion'
import YearQuestion from 'components/SignupFormQuestions/YearQuestion'
import LoginQuestion from 'components/SignupFormQuestions/LoginQuestion'
import AccountEmailQuestion from 'components/SignupFormQuestions/AccountEmailQuestion'
import DemographicQuestion from 'components/SignupFormQuestions/DemographicQuestion'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { resetErrors } from 'redux/slices/SignupFormAnswers'
import sanitize from 'lib/HtmlSanitizer'

const SignupFormQuestionsHandler = ({
  questions,
  options,
  conditionalQuestions,
}: SignUpFormQuestionHandler): JSX.Element => {
  const signupFormAnswers = useAppSelector(
    (state) => state.signupAnswers.answers,
  )
  const { search } = useLocation()

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(resetErrors())
  }, [])

  const site = useAppSelector((state) => state.site.site)
  const region = site.region.toLowerCase()
  const readOnlyMode = new URLSearchParams(search).get('edit') === null

  const answers: IndexType = signupFormAnswers

  const renderYearQuestion = (
    question: string,
    id: string,
    required: boolean,
  ) => {
    return (
      <YearQuestion
        question={question}
        id={id}
        readOnlyMode={readOnlyMode}
        required={required}
      />
    )
  }

  const renderDateQuestion = (
    question: string,
    id: string,
    required: boolean,
  ) => {
    return (
      <DateQuestion
        question={question}
        id={id}
        readOnlyMode={readOnlyMode}
        required={required}
      />
    )
  }

  const renderQuestions = (item: QuestionsItems) => {
    const {
      question,
      descriptor,
      type,
      id,
      imageUrl,
      maxLength,
      yearOnlyDate,
      required,
      horizontal,
    } = item

    const sanitizedQuestion = sanitize(question).__html
    const regionAnswer = signupFormAnswers[id]?.value || ''

    switch (type) {
      case 'SignupForm::AccountEmailQuestion':
        return (
          <AccountEmailQuestion
            question={sanitizedQuestion}
            id={id}
            readOnlyMode={readOnlyMode}
            required={true}
          />
        )
      case 'EmailQuestion':
        return (
          <EmailQuestion
            question={sanitizedQuestion}
            id={id}
            readOnlyMode={readOnlyMode}
            required={required}
          />
        )
      case 'EssayQuestion':
        return (
          <EssayQuestion
            question={sanitizedQuestion}
            id={id}
            readOnlyMode={readOnlyMode}
            required={required}
          />
        )
      case 'SectionQuestion':
        return (
          <SectionQuestion
            question={sanitizedQuestion}
            id={id}
            description={descriptor}
          />
        )

      case 'SignupForm::LoginQuestion':
        return (
          <LoginQuestion
            id={id}
            question={sanitizedQuestion}
            maxLength={191}
            readOnlyMode={readOnlyMode}
            required={true}
          />
        )

      case 'SingleLineQuestion':
        return (
          // id -> refers to the answer id not the question id
          <Box key={id}>
            <SingleLineQuestion
              question={sanitizedQuestion}
              id={id}
              readOnlyMode={readOnlyMode}
              required={required}
              maxLength={maxLength}
            />
          </Box>
        )

      case 'ImageQuestion':
        return (
          <ImageQuestion
            question={sanitizedQuestion}
            id={id}
            imageUrl={imageUrl as string}
          />
        )

      case 'DropDownQuestion':
        return (
          <DropdownQuestion
            question={sanitizedQuestion}
            id={id}
            options={extractQuestionOptions(options, id)}
            readOnlyMode={readOnlyMode}
            required={required}
          />
        )

      case 'NumberQuestion':
        return (
          <NumberQuestion
            question={sanitizedQuestion}
            id={id}
            maxLength={maxLength as number}
            readOnlyMode={readOnlyMode}
            required={required}
          />
        )

      case 'RadioButtonQuestion':
        return (
          <RadioButtonQuestion
            question={sanitizedQuestion}
            id={id}
            options={extractQuestionOptions(options, id)}
            readOnlyMode={readOnlyMode}
            required={required}
            inLineOptions={horizontal}
          />
        )

      case 'CheckBoxQuestion':
        return (
          <CheckboxQuestion
            question={sanitizedQuestion}
            id={id}
            required={required}
            options={extractQuestionOptions(options, id)}
            readOnlyMode={readOnlyMode}
            inLineOptions={horizontal}
          />
        )

      case 'DateQuestion':
        if (yearOnlyDate) {
          return renderYearQuestion(sanitizedQuestion, id, required)
        }
        return renderDateQuestion(sanitizedQuestion, id, required)

      case 'RegionQuestion':
        return (
          <RegionQuestion
            question={sanitizedQuestion}
            id={id}
            readOnlyMode={readOnlyMode}
            required={required}
            answer={getSuburbDefaultValue(regionAnswer, region) || null}
          />
        )

      case 'Demographics::LocationQuestion':
      case 'Demographics::AgeQuestion':
        return (
          <Box key={id}>
            <DemographicQuestion
              question={question}
              id={id}
              readOnlyMode={readOnlyMode}
            />
          </Box>
        )

      default:
        return <></>
    }
  }

  const findTriggeredQuestions = (parent: IndexType, children: IndexType) => {
    // Check if question has been answered
    if (!checkObjectProperty(answers, parent.id)) {
      return false
    }
    const answer: IndexType = answers[parent.id]

    const questions = triggeredQuestions(parent, answer, children)

    if (questions.length > 0) {
      return (
        <SignupFormQuestionsHandler
          answers={answers}
          questions={questions}
          conditionalQuestions={conditionalQuestions}
          options={options}
        />
      )
    }

    return false
  }
  return (
    <>
      {questions.map((question) => {
        return (
          <Box key={question.id} pt={2}>
            {renderQuestions(question)}
            {checkObjectProperty(conditionalQuestions, question.id) &&
              findTriggeredQuestions(
                question,
                conditionalQuestions[question.id],
              )}
          </Box>
        )
      })}
    </>
  )
}

export default SignupFormQuestionsHandler
